import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Admin Create / Edit competition modal. Used by Dashboard.js for both modes
 * — the only difference between create and edit is the heading, submit label,
 * and the presence of a destructive "Delete competition" footer in edit mode.
 *
 * Extracted from Dashboard.js because that file had grown past 1200 lines
 * (testing agent flagged it in iters 27 + 28). Zero behaviour change — same
 * props, same data-testids, same form state shape.
 *
 * The "Total rounds" input intentionally allows empty mid-edit (state may be
 * "") so admins can clear "10" and type "6" without the value snapping to
 * "1" between keystrokes (iter 27 bug repro). onBlur re-clamps to ≥1 and
 * the parent's submit handler clamps again defensively.
 */
export default function CompetitionFormDialog({
  mode,
  open,
  onOpenChange,
  form,
  setForm,
  courseOptions,
  courseDetails,
  fetchCourseDetails,
  saving,
  onSubmit,
  onRequestDelete,
}) {
  const isEdit = mode === "edit";
  const heading = isEdit ? "Edit competition" : "Create new competition";
  const submitLabel = isEdit
    ? (saving ? "Saving…" : "Save changes")
    : (saving ? "Creating…" : "Create competition");
  const tees = form.course_id ? (courseDetails[form.course_id]?.tees || []) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg">{heading}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4" data-testid={`comp-form-${mode}`}>
          <div>
            <Label htmlFor={`comp-${mode}-name`}>Competition name</Label>
            <Input
              id={`comp-${mode}-name`}
              data-testid={`comp-${mode}-name`}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              placeholder="Spring Eclectic 2026"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor={`comp-${mode}-start`}>Start date</Label>
              <Input
                id={`comp-${mode}-start`}
                data-testid={`comp-${mode}-start`}
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor={`comp-${mode}-end`}>End date</Label>
              <Input
                id={`comp-${mode}-end`}
                data-testid={`comp-${mode}-end`}
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                required
              />
            </div>
          </div>
          <div>
            <Label htmlFor={`comp-${mode}-rounds`}>Total rounds (max)</Label>
            <Input
              id={`comp-${mode}-rounds`}
              data-testid={`comp-${mode}-rounds`}
              type="number"
              inputMode="numeric"
              min="1"
              value={form.total_rounds}
              onFocus={(e) => e.target.select()}
              onChange={(e) => {
                // Allow empty mid-edit so the admin can clear "10" and type
                // "6" without the field snapping back to "1" between
                // keystrokes (which produced bug-report value "16" — the
                // stuck "1" followed by the user's "6"). We re-clamp on
                // blur / submit instead of every keystroke.
                const raw = e.target.value;
                if (raw === "") {
                  setForm({ ...form, total_rounds: "" });
                  return;
                }
                const n = parseInt(raw, 10);
                if (Number.isNaN(n)) return;
                setForm({ ...form, total_rounds: n });
              }}
              onBlur={() => {
                const n = parseInt(form.total_rounds, 10);
                setForm({ ...form, total_rounds: Number.isNaN(n) || n < 1 ? 1 : n });
              }}
              required
            />
          </div>
          <div>
            <Label className="mb-2 block">Hole format</Label>
            <div className="space-y-1.5">
              {[
                { v: "18", l: "18 holes (full round)" },
                { v: "front_9", l: "9 holes — Front 9 (1–9)" },
                { v: "back_9", l: "9 holes — Back 9 (10–18)" },
              ].map((opt) => (
                <label key={opt.v} className="flex items-center gap-2 cursor-pointer text-sm text-ink">
                  <input
                    type="radio"
                    name={`hole_format_${mode}`}
                    value={opt.v}
                    checked={form.hole_format === opt.v}
                    onChange={(e) => setForm({ ...form, hole_format: e.target.value })}
                    className="w-4 h-4 text-accent"
                  />
                  {opt.l}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor={`comp-${mode}-course`} className="mb-1.5 block">Course (optional)</Label>
            <select
              id={`comp-${mode}-course`}
              data-testid={`comp-${mode}-course`}
              value={form.course_id || ""}
              onChange={(e) => {
                const v = e.target.value;
                setForm({ ...form, course_id: v, tee_id: "" });
                if (v) fetchCourseDetails(v);
              }}
              className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-ink text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
            >
              <option value="">— No course —</option>
              {(courseOptions || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.club_name && c.club_name !== c.name ? ` (${c.club_name})` : ""}{c.location ? ` · ${c.location}` : ""}
                </option>
              ))}
            </select>
            <p className="text-[11.5px] text-ink-muted mt-1">Linking a course unlocks par-aware leaderboards.</p>
          </div>
          {form.course_id && tees.length > 1 && (
            <div>
              <Label htmlFor={`comp-${mode}-tee`} className="mb-1.5 block">Tee (optional)</Label>
              <select
                id={`comp-${mode}-tee`}
                data-testid={`comp-${mode}-tee`}
                value={form.tee_id || ""}
                onChange={(e) => setForm({ ...form, tee_id: e.target.value })}
                className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-ink text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
              >
                <option value="">— Default (first tee) —</option>
                {tees.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.gender}) · par {t.par_total}{t.course_rating ? ` · CR ${t.course_rating}` : ""}{t.slope_rating ? ` · SR ${t.slope_rating}` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          <label className="flex items-center gap-2 p-3 border border-accent/30 bg-accent-tint-2 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.recognize_score_of_2}
              onChange={(e) => setForm({ ...form, recognize_score_of_2: e.target.checked })}
              className="w-4 h-4 text-accent-deep"
            />
            <span className="text-sm text-accent-deep">
              <strong>Recognise score of 2</strong> — celebrate and highlight excellent 2s
            </span>
          </label>
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1 bg-ink hover:bg-ink-hover" data-testid={`comp-${mode}-submit`}>
              {submitLabel}
            </Button>
          </div>
          {isEdit && onRequestDelete && (
            <div className="pt-3 mt-2 border-t border-line">
              <Button
                type="button"
                variant="ghost"
                onClick={onRequestDelete}
                disabled={saving}
                data-testid={`comp-${mode}-delete`}
                className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                Delete competition
              </Button>
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
